import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Switch,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from '@mui/material';
import {
  Check as CheckIcon,
  Info as InfoIcon,
  Diamond as DiamondIcon,
} from '@mui/icons-material';
import ChargebeeService from '../services/chargebee.service';

const BlackFridayPricingPage = () => {
  const [searchParams] = useSearchParams();
  
  // Get parameters from URL
  const subscriptionId = searchParams.get('subscriptionId') || '';
  const currentPlanId = searchParams.get('currentPlanId') || '';
  const amount = parseFloat(searchParams.get('amount') || '0');
  const id = searchParams.get('id') || 'pricing-section';
  const redirectUrl = searchParams.get('redirectUrl') || '';
  const cancelUrl = window.location.href;

  console.log('currentPlanId', currentPlanId);
  console.log('amount', amount);
  const [pricingMode, setPricingMode] = useState('lifetime'); // 'lifetime' or 'regular'
  const [paymentType, setPaymentType] = useState({
    professional: 'oneTime',
    professionalPlus: 'oneTime',
    scale: 'oneTime',
  });
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const isLifetime = pricingMode === 'lifetime';

  // Pricing data structure
  const pricingPlans = {
    professional: {
      name: 'Professional',
      badge: 'Live + Automated',
      description: 'The perfect all-in-one solution for creators and small teams just getting started with live and automated events.',
      attendees: '200 Live + 1,000 Automated Attendees',
      lifetime: {
        oneTime: 2399,
        installments: 3,
        monthly: 833,
        monthlyTotal: 1392,
      },
      regular: {
        monthly: 116,
        yearly: 696,
        yearlyOriginal: 1392,
      },
      savings: {
        lifetimePrice: 2399,
        monthlyEquivalent: 24.99,
        yearlySavings: 78.5,
      },
      bonuses: [
        'EasySuite CRM (your inbuilt sales CRM)',
        'Community and workshop access',
        { text: 'Complete Webinar Funnel Setup', value: 1000 },
      ],
      features: [
        '4-hour live sessions',
        'Up to 5 active webinars',
        'Simulive, On-Demand & Evergreen',
        'Stream directly to YouTube or via RTMP',
        'Simulated + live chat support',
        'Up to 3 co-hosts',
        'Advanced analytics & reporting',
        '4 GB storage',
        'Priority support & manager',
        'Full CRM integrations',
      ],
    },
    professionalPlus: {
      name: 'Professional Plus',
      badge: 'Live + Automated',
      popular: true,
      description: 'All-in-one solution for growing businesses who want both live and automated webinars with higher capacity.',
      attendees: '500 Live + 2,500 Automated Attendees',
      lifetime: {
        oneTime: 3995,
        installments: 3,
        monthly: 1365,
        monthlyTotal: 2376,
      },
      regular: {
        monthly: 198,
        yearly: 1188,
        yearlyOriginal: 2376,
      },
      savings: {
        lifetimePrice: 3995,
        monthlyEquivalent: 41.60,
        yearlySavings: 79,
      },
      bonuses: [
        'EasySuite CRM (your inbuilt sales CRM)',
        { text: 'AI Webinar Builder', badge: 'BETA' },
        { text: 'Paid Webinars', badge: 'BETA' },
        'Community and workshop access',
        { text: 'Complete Webinar Funnel Setup', value: 1000 },
      ],
      features: [
        '4-hour live sessions',
        'Up to 5 active webinars',
        'Simulive, On-Demand & Evergreen',
        'Stream directly to YouTube or via RTMP',
        'Simulated + live chat support',
        'Up to 3 co-hosts',
        'Advanced analytics & reporting',
        '4 GB storage',
        'Priority support & manager',
        'Full CRM integrations',
      ],
    },
    scale: {
      name: 'Scale',
      badge: 'Automated Only',
      description: 'If you want only automated sessions, with higher attendee limits for lead generation & 24/7 sales.',
      attendees: '6,000 Automated Attendees',
      lifetime: {
        oneTime: 4999,
        installments: 3,
        monthly: 1700,
        monthlyTotal: 2904,
      },
      regular: {
        monthly: 242,
        yearly: 1452,
        yearlyOriginal: 2904,
      },
      savings: {
        lifetimePrice: 4999,
        monthlyEquivalent: 52.07,
        yearlySavings: 78.5,
      },
      bonuses: [
        'EasySuite CRM (your inbuilt sales CRM)',
        { text: 'AI Webinar Builder', badge: 'BETA' },
        { text: 'Paid Webinars', badge: 'BETA' },
        'Community and workshop access',
        { text: 'Complete Webinar Funnel Setup', value: 1000 },
      ],
      features: [
        'Unlimited Sessions (Automated)',
        'Simulive, On-Demand & Evergreen',
        'Simulated + live chat options',
        'Evergreen webinars with CTAs & polls',
        'Funnel optimization features',
        'Advanced analytics & reporting',
        'Priority support & manager',
        '4 GB storage',
        'Built for always-on conversions',
        'Full CRM Integration',
      ],
    },

  };

  const handlePaymentTypeChange = (plan: string, type: string) => {
    setPaymentType((prev) => ({
      ...prev,
      [plan]: type,
    }));
  };

  /**
   * Get planId and units based on pricing mode, payment type, and plan tier
   * @param planKey - The plan key: 'professional', 'professionalPlus', or 'scale'
   * @param isLifetime - Whether lifetime pricing is selected
   * @param paymentTypeValue - Payment type: 'oneTime' or 'installments' (only for lifetime)
   * @returns Object with planId and units
   */
  const getPlanDetails = (
    planKey: string,
    isLifetime: boolean,
    paymentTypeValue: string
  ): { planId: string; units: number } => {
    // Yearly plans
    if (!isLifetime) {
      switch (planKey) {
        case 'professional':
          return { planId: 'All-In-One-USD-Yearly', units: 200 };
        case 'professionalPlus':
          return { planId: 'All-In-One-USD-Yearly', units: 500 };
        case 'scale':
          return { planId: 'Automated-Gold-USD-Yearly', units: 6000 };
        default:
          throw new Error(`Unknown plan key: ${planKey}`);
      }
    }

    // Lifetime plans
    if (paymentTypeValue === 'oneTime') {
      // Lifetime (Billed One Time)
      switch (planKey) {
        case 'professional':
          return { planId: 'All-In-One-USD-Every-15-years', units: 200 };
        case 'professionalPlus':
          return { planId: 'All-In-One-USD-Every-15-years', units: 500 };
        case 'scale':
          return { planId: 'Automated-Gold-USD-Every-15-years', units: 6000 };
        default:
          throw new Error(`Unknown plan key: ${planKey}`);
      }
    } else {
      // Lifetime (3 Installments)
      switch (planKey) {
        case 'professional':
          return { planId: 'All-In-One-USD-Monthly', units: 200 };
        case 'professionalPlus':
          return { planId: 'All-In-One-USD-Monthly', units: 500 };
        case 'scale':
          return { planId: 'Automated-Gold-USD-Monthly', units: 6000 };
        default:
          throw new Error(`Unknown plan key: ${planKey}`);
      }
    }
  };

  const calculatePrice = (plan: any, planKey: string) => {
    if ('custom' in plan && plan.custom) return 'Custom Pricing';
    
    if (isLifetime && 'lifetime' in plan) {
      const validKey = planKey as keyof typeof paymentType;
      if (validKey in paymentType && paymentType[validKey] === 'oneTime') {
        return `$${plan.lifetime.oneTime.toLocaleString()}`;
      } else {
        return `$${plan.lifetime.monthly}/month`;
      }
    } else if ('regular' in plan) {
      // When in regular mode, show yearly pricing (discounted)
      if ('yearly' in plan.regular) {
        return `$${plan.regular.yearly.toLocaleString()}/year`;
      }
      return `$${plan.regular.monthly}/month`;
    }
    return 'N/A';
  };

  const getPriceValue = (plan: any, planKey: string) => {
    if ('custom' in plan && plan.custom) return null;
    
    if (isLifetime && 'lifetime' in plan) {
      const validKey = planKey as keyof typeof paymentType;
      if (validKey in paymentType && paymentType[validKey] === 'oneTime') {
        return plan.lifetime.oneTime;
      } else {
        return plan.lifetime.monthly;
      }
    } else if ('regular' in plan) {
      // When in regular mode, return yearly price (discounted) since that's what's displayed
      if ('yearly' in plan.regular) {
        return plan.regular.yearly;
      }
      return plan.regular.monthly;
    }
    return null;
  };

  const handleBuyNow = async (plan: any, planKey: string) => {
    if ('custom' in plan && plan.custom) {
      // Handle custom plan - schedule a call
      window.open('https://calendly.com/your-calendar-link', '_blank');
      return;
    }

    // Set loading state for this specific plan
    setLoadingPlan(planKey);

    try {
      const planName = plan.name || 'Unknown Plan';
      const currentPaymentType = paymentType[planKey as keyof typeof paymentType];
      
      // Get planId and units based on selection
      const { planId, units } = getPlanDetails(planKey, isLifetime, currentPaymentType);

      let billingCycles;
      let itemTiers;
      if(isLifetime && currentPaymentType === 'installments') {
        billingCycles = 3;

        itemTiers = [
          {
            item_price_id: planId,
            starting_unit: 1,
            ending_unit: units,
            price: plan.lifetime.monthly * 100 // Convert to cents
          },
          {
            item_price_id: planId,
            starting_unit: units + 1,
            price: plan.lifetime.monthly * 100 // Convert to cents
          }
        ];
      }


      let couponId = undefined;
      if(!isLifetime) {
        couponId = 'BLACKFRIDAY25Y';
      }


      // Call ChargebeeService to upgrade plan
      const hostedPage = await ChargebeeService.upgradePlanUsingCheckout(
        subscriptionId,
        planId,
        units,
        undefined,
        couponId,
        billingCycles,
        itemTiers,
        redirectUrl,
        cancelUrl
      );

      // Redirect to hosted checkout page if URL is available
      if (hostedPage?.hosted_page?.url) {
        // Keep loading state active until redirect happens
        window.location.href = hostedPage.hosted_page.url;
      } else {
        console.error('No hosted page URL received');
        setLoadingPlan(null); // Reset loading on error
        alert('Failed to initiate checkout. Please try again.');
      }
    } catch (error: any) {
      console.error('Error initiating purchase:', error);
      setLoadingPlan(null); // Reset loading on error
      alert(error.message || 'Failed to initiate purchase. Please try again.');
    }
  };

  const renderBonusItem = (bonus: string | { text: string; value?: number; badge?: string }) => {
    if (typeof bonus === 'string') {
      return bonus;
    }
    if (bonus.value) {
      return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography component="span" sx={{ fontSize: '0.875rem' }}>{bonus.text}</Typography>
            <Chip
            label={`worth $${bonus.value}`} 
            size="small" 
            sx={{ 
              bgcolor: '#737373',
              color: 'white',
              height: '16px',
              fontSize: '10px',
              borderRadius: '4px',
            }} 
          />
        </Box>
      );
    }
    if (bonus.badge) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>{bonus.text}</span>
          <Chip 
            label={bonus.badge} 
            size="small" 
            sx={{ 
              bgcolor: '#737373',
              color: 'white',
              height: '16px',
              fontWeight: 'medium',
              borderRadius: '4px',
              fontSize: '10px',
            }} 
          />
        </Box>
      );
    }
  };

  return (
    <Box id={id} sx={{ py: 6 }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {/* Pricing Mode Toggle */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
              Choose Your Perfect Plan
            </Typography>
            
            <ToggleButtonGroup
              value={pricingMode}
              exclusive
              onChange={(e, newMode) => newMode && setPricingMode(newMode)}
              sx={{
                bgcolor: 'white',
                borderRadius: 3,
                boxShadow: 2,
                '& .MuiToggleButton-root': {
                  px: 4,
                  py: 2,
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  '&.Mui-selected': {
                    bgcolor: '#4caf50',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#45a049',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="regular">
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Yearly - 50% OFF
                  </Typography>
                  <Typography variant="caption">
                    Pay annually, save big
                  </Typography>
                </Box>
              </ToggleButton>
              <ToggleButton value="lifetime">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Lifetime - Save up to 79%
                    </Typography>
                    <Typography variant="caption">
                      Pay once, use forever
                    </Typography>
                  </Box>
                  <Chip
                    label="BEST VALUE"
                    size="small"
                    sx={{
                      bgcolor: '#ff9800',
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Black Friday Link */}
          {/* <Link
            href="#"
            sx={{
              color: '#2196f3',
              textDecoration: 'none',
              fontWeight: 500,
              fontSize: '1.1rem',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            🎉 Show me the Black Friday bonuses
          </Link> */}
        </Box>

        {/* Pricing Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 4,
          }}
        >
          {Object.entries(pricingPlans)
            .filter(([key, plan]: [string, any]) => {
              // Hide Professional plan in yearly mode if currentPlanId is pro-plan-yearly or standard-plan-yearly
              // But always show it in lifetime mode
              if (key === 'professional' && 
                  (currentPlanId === 'pro-plan-yearly' || currentPlanId === 'standard-plan-yearly') && 
                  amount > 600 && 
                  !isLifetime) {
                return false;
              }
              return true;
            })
            .map(([key, plan]: [string, any]) => (
            <Box key={key} sx={{ position: 'relative' }}>
              {/* Most Popular Badge - Outside and Above Card */}
              {('popular' in plan && plan.popular) && (
                <Box
                  sx={{
                    background: '#1313FF',
                    color: 'white',
                    textAlign: 'center',
                    py: 1,
                    fontWeight: 600,
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    mb: -1,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  Most Popular
                </Box>
              )}
              
              {/* Empty space for alignment when not popular */}
              {!('popular' in plan && plan.popular) && (
                <Box sx={{ height: '42px' }} />
              )}
              
              <Card
                sx={{
                  position: 'relative',
                  border: ('popular' in plan && plan.popular) ? '1px solid #2196f3' : '1px solid #e0e0e0',
                  borderRadius: ('popular' in plan && plan.popular) ? '8px 8px 8px 8px' : 8,
                  borderTopLeftRadius: ('popular' in plan && plan.popular) ? '8px 8px 8px 8px' : 8,
                  borderTopRightRadius: ('popular' in plan && plan.popular) ? '8px 8px 8px 8px' : 8,
                  boxShadow: ('popular' in plan && plan.popular) ? '0 8px 24px rgba(33, 150, 243, 0.2)' : 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >

              <CardContent sx={{ p: 3 }}>
                {/* Plan Badge */}
                <Chip
                  label={plan.badge}
                  size="small"
                  sx={{
                    mb: 2,
                    border: '1px solid #e0e0e0',
                    bgcolor: 'white',
                    fontWeight: 500,
                  }}
                />

                {/* Plan Name */}
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  {plan.name}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, minHeight: 60, color: '#181818' }}
                >
                  {plan.description}
                </Typography>

                {/* Attendees */}
                <Box sx={{ mb: 3, border: '1px solid #e7e7e7', padding: '5px 10px', borderRadius: '8px', backgroundColor: 'linear-gradient(135deg, #EFF6FF 0%, #FAF5FF 100%)' }}>
                  <Typography variant="caption" color="#000" sx={{ mb: 0.5, display: 'block', fontWeight: 600, }}>
                    Attendees
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#E213E2' }}>
                    {plan.attendees} /month
                  </Typography>
                </Box>

                {/* Price */}
                {!isLifetime && 'regular' in plan && 'yearlyOriginal' in plan.regular ? (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        variant="h3"
                        component="span"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.5rem !important',
                          color: ('popular' in plan && plan.popular) ? '#2196f3' : 'text.primary',
                          textDecoration: 'line-through',
                          opacity: 0.6,
                        }}
                      >
                        ${(plan.regular as { yearlyOriginal: number }).yearlyOriginal.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="h3"
                        component="span"
                        sx={{
                          fontWeight: 700,
                          fontSize: '2rem !important',
                          color: ('popular' in plan && plan.popular) ? '#111827' : 'text.primary',
                        }}
                      >
                        ${plan.regular.yearly.toLocaleString()}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '1rem !important',
                            color: '#6b7280',
                            fontWeight: 400,
                          }}
                        >
                          /year
                        </Typography>
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        color: '#6b7280',
                        mt: 0.5,
                      }}
                    >
                      Breaks down to ${Math.round(plan.regular.yearly / 12)}/month
                    </Typography>
                  </Box>
                ) : (
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      fontSize: '2rem !important',
                      color: ('popular' in plan && plan.popular) ? '#2196f3' : 'text.primary',
                    }}
                  >
                    {calculatePrice(plan, key)}
                  </Typography>
                )}

                {('custom' in plan && plan.custom) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    With BlackFriday Offer Applied
                  </Typography>
                )}

                {/* Payment Options for non-custom plans */}
                {!('custom' in plan && plan.custom) && isLifetime && (
                  <FormControl sx={{ mb: 2, width: '100%' }}>
                    <RadioGroup
                      value={paymentType[key as keyof typeof paymentType]}
                      onChange={(e) => handlePaymentTypeChange(key, e.target.value)}
                      sx={{ flexDirection: 'row', }}
                    >
                      <FormControlLabel
                        value="oneTime"
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#5B5D5E' }}>
                            Billed One Time
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="installments"
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#5B5D5E' }}>
                            3 Equal Installments
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                )}

                {/* CTA Button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => handleBuyNow(plan, key)}
                  disabled={loadingPlan === key}
                  sx={{
                    mb: 3,
                    py: 1.5,
                    bgcolor: ('custom' in plan && plan.custom) ? 'white' : ('popular' in plan && plan.popular) ? '#2196f3' : 'black',
                    color: ('custom' in plan && plan.custom) ? 'black' : 'white',
                    fontWeight: 600,
                    border: ('custom' in plan && plan.custom) ? '2px solid black' : 'none',
                    '&:hover': {
                      bgcolor: ('custom' in plan && plan.custom) ? '#f5f5f5' : ('popular' in plan && plan.popular) ? '#1976d2' : '#333',
                    },
                    '&:disabled': {
                      bgcolor: ('custom' in plan && plan.custom) ? 'white' : ('popular' in plan && plan.popular) ? '#2196f3' : 'black',
                      color: ('custom' in plan && plan.custom) ? 'black' : 'white',
                      opacity: 0.7,
                    },
                  }}
                >
                  {loadingPlan === key ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: ('custom' in plan && plan.custom) ? 'black' : 'white' }} />
                      <span>Loading...</span>
                    </Box>
                  ) : (
                    ('custom' in plan && plan.custom) ? 'Schedule a Call' : 'Buy Now'
                  )}
                </Button>

                {/* Savings Breakdown */}
                {!('custom' in plan && plan.custom) && isLifetime && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Savings Breakdown
                      </Typography>
                      {/* <Tooltip title="See how much you save with lifetime pricing">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip> */}
                    </Box>
                    <Box
                      component="ul"
                      sx={{
                        fontSize: '0.875rem',
                        paddingTop: '0',
                        paddingLeft: '20px',
                        margin: 0,
                        listStyle: 'disc',
                        '& li': {
                          marginBottom: '8px',
                          paddingLeft: '0',
                          listStyle: 'disc',
                        },
                      }}
                    >
                      <Box component="li">
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', display: 'inline' }}>
                          Regular price: ${'lifetime' in plan ? plan.lifetime.monthly : 'N/A'}/month (≈ ${'lifetime' in plan ? plan.lifetime.monthlyTotal : 'N/A'}/year)
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', display: 'inline' }}>
                          Lifetime deal: ${'savings' in plan ? plan.savings.lifetimePrice : 'N/A'}
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', display: 'inline' }}>
                          Based on 8 yr use you save ~{'savings' in plan ? plan.savings.yearlySavings : 'N/A'}% vs paying annually
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', display: 'inline' }}>
                          Equivalent to ~${'savings' in plan ? plan.savings.monthlyEquivalent : 'N/A'} / month over 8 years
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Black Friday Bonuses */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#22B060' }}>
                      Holiday Sales Exclusive Bonuses
                    </Typography>
                    <Chip
                      label="LIMITED TIME"
                      size="small"
                      sx={{
                        bgcolor: '#22B060',
                        color: 'white',
                        height: '16px',
                        fontWeight: 'bold',
                        fontSize: '10px',
                        borderRadius: '4px',
                      }}
                    />
                  </Box>
                  <List dense>
                    {plan.bonuses.map((bonus: any, index: number) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5, gap: 1  }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {typeof bonus === 'string' || !('badge' in bonus && bonus.badge) ? (
                            <CheckIcon fontSize="small" sx={{ color: '#000' }} />
                          ) : (
                            <DiamondIcon fontSize="small" color="warning" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {renderBonusItem(bonus)}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Core Features */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Core Features Included
                  </Typography>
                  <List dense>
                    {plan.features.slice(0, 10).map((feature: string, index: number) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5, gap: 1 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {feature}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                  {/* <Link
                    href="#"
                    sx={{
                      color: '#2196f3',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      display: 'block',
                      mt: 2,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    See all features
                  </Link> */}
                </Box>
              </CardContent>
            </Card>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default BlackFridayPricingPage;